import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages } from '../data/mock';
import {
  ArrowRight, ArrowUpRight, Truck, ShieldCheck, RotateCw, Headphones,
  Dumbbell, Timer, Flame, Activity, Star, TrendingUp, Zap
} from 'lucide-react';

const HomePage = () => {
  const featuredProducts = products.slice(0, 4);
  const allProducts = products;

  useEffect(() => {
    document.title = 'Sport Fitt Pro | Sports Gear & Fitness Equipment';
  }, []);

  const performancePillars = [
    { icon: Dumbbell, title: 'Strength', desc: 'Build raw power with precision-engineered training tools.' },
    { icon: Timer, title: 'Endurance', desc: 'Outlast the competition with gear built for the long haul.' },
    { icon: Activity, title: 'Mobility', desc: 'Move freely. Train smarter. Prevent the setbacks.' },
    { icon: Flame, title: 'Recovery', desc: 'Bounce back faster with pro-grade recovery equipment.' },
  ];

  return (
    <div className="min-h-screen bg-white text-ink-900 font-body">
      <Header />
      <CartDrawer />

      <main>
        {/* ============ 1. HERO SECTION ============ */}
        <section className="relative bg-ink-950 text-white overflow-hidden pt-[104px]">
          <div className="absolute inset-0">
            <img
              src={siteImages.heroMain}
              alt="Athletic training"
              className="w-full h-full object-cover opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-ink-950 via-ink-950/85 to-athletic-950/60" />
          </div>

          {/* Decorative slashes */}
          <div className="absolute top-0 right-0 w-2/5 h-full pointer-events-none opacity-10">
            <div className="absolute inset-0" style={{
              background: 'repeating-linear-gradient(110deg, transparent 0 40px, rgba(255,77,26,0.3) 40px 42px)'
            }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              {/* Copy */}
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className="block w-8 h-0.5 bg-athletic-500" />
                  <span className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase">
                    Performance Sport Brand
                  </span>
                </div>

                <h1 className="font-display leading-[0.9] tracking-wider mb-6">
                  <span className="block text-[clamp(3rem,9vw,7rem)]">UNLEASH</span>
                  <span className="block text-[clamp(3rem,9vw,7rem)] text-athletic-500">YOUR INNER</span>
                  <span className="block text-[clamp(3rem,9vw,7rem)]">ATHLETE</span>
                </h1>

                <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-10">
                  Premium sports gear engineered for the gym, the track and everything in between.
                  Built to push, tested to last.
                </p>

                <div className="flex flex-wrap gap-4 mb-12">
                  <Link
                    to="/shop"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-all"
                  >
                    Shop Now
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/why-choose-us"
                    className="group inline-flex items-center gap-3 px-8 py-4 border-2 border-white/20 hover:border-white text-white font-black uppercase tracking-wider transition-all"
                  >
                    Our Story
                    <ArrowUpRight className="w-5 h-5" />
                  </Link>
                </div>

                {/* Micro stats */}
                <div className="grid grid-cols-3 gap-6 max-w-lg border-l-4 border-athletic-500 pl-6">
                  {[
                    { value: '8+', label: 'Pro Products' },
                    { value: '$75+', label: 'Free Shipping' },
                    { value: '30d', label: 'Easy Returns' },
                  ].map((s, i) => (
                    <div key={i}>
                      <p className="font-display text-4xl text-white leading-none">{s.value}</p>
                      <p className="text-[10px] text-white/50 uppercase tracking-[0.25em] mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual block */}
              <div className="lg:col-span-5 relative hidden lg:block">
                <div className="relative aspect-[4/5]">
                  <div className="absolute inset-0 bg-athletic-500" style={{ clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0 100%)' }}>
                    <img
                      src={siteImages.heroLifestyle}
                      alt="Training lifestyle"
                      className="w-full h-full object-cover mix-blend-multiply opacity-90"
                    />
                  </div>

                  {/* Floating stat card */}
                  <div className="absolute -left-6 bottom-10 bg-white text-ink-900 px-6 py-4 shadow-2xl max-w-[220px]">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-athletic-500 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <p className="font-display text-2xl leading-none">TOP TIER</p>
                    </div>
                    <p className="text-xs text-ink-600 leading-snug">Quality-tested by athletes, engineered for performance.</p>
                  </div>

                  {/* Floating corner element */}
                  <div className="absolute top-4 -right-4 bg-ink-950 text-volt-400 px-4 py-3 font-black text-xs uppercase tracking-wider border-l-4 border-volt-400">
                    NEW DROP<br />
                    <span className="text-white/60 text-[10px] tracking-widest">2025 Collection</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 2. TICKER MARQUEE ============ */}
        <section className="bg-athletic-500 text-white overflow-hidden border-y-2 border-ink-950">
          <div className="flex whitespace-nowrap animate-ticker py-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-10 px-6 font-display text-2xl tracking-[0.15em]">
                <span>GEAR UP. LEVEL UP.</span>
                <Zap className="w-5 h-5 text-volt-400" />
                <span>FREE USA SHIPPING $75+</span>
                <Zap className="w-5 h-5 text-volt-400" />
                <span>TRAIN SMARTER.</span>
                <Zap className="w-5 h-5 text-volt-400" />
                <span>NEW 2025 COLLECTION</span>
                <Zap className="w-5 h-5 text-volt-400" />
              </div>
            ))}
          </div>
        </section>

        {/* ============ 3. FEATURED PRODUCTS ============ */}
        <section className="py-20 lg:py-28 bg-ink-50">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Best Sellers</p>
                <h2 className="font-display text-5xl lg:text-7xl tracking-wider leading-none">
                  TOP <span className="text-athletic-500">PERFORMERS</span>
                </h2>
                <p className="text-ink-500 mt-4 max-w-lg">
                  Handpicked gear from our best sellers — tested, trusted and built for serious athletes.
                </p>
              </div>
              <Link
                to="/shop"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-ink-900 hover:bg-athletic-500 text-white font-black uppercase tracking-wider text-sm transition-all self-start lg:self-auto"
              >
                View All Products
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* ============ 4. CATEGORY SHOWCASE ============ */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-14">
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Shop By Category</p>
              <h2 className="font-display text-5xl lg:text-7xl tracking-wider leading-none">
                FIND YOUR <span className="text-athletic-500">EDGE</span>
              </h2>
            </div>

            {/* First row - 2 large cards */}
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              {categories.slice(0, 2).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="group relative aspect-[16/9] overflow-hidden bg-ink-950"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-ink-950 via-ink-950/40 to-transparent" />
                  <div className="absolute inset-0 p-8 flex flex-col justify-between">
                    <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase">Category</p>
                    <div>
                      <h3 className="font-display text-4xl lg:text-5xl text-white tracking-wider leading-none mb-2">
                        {cat.name.toUpperCase()}
                      </h3>
                      <p className="text-white/70 text-sm mb-4 max-w-md">{cat.description}</p>
                      <span className="inline-flex items-center gap-2 text-white font-black uppercase tracking-wider text-xs">
                        Shop Collection
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Second row - remaining categories */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.slice(2).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="group relative aspect-square bg-ink-100 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/50 to-transparent" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-end">
                    <h3 className="font-display text-white text-xl leading-tight tracking-wider group-hover:text-athletic-400 transition-colors">
                      {cat.name.toUpperCase()}
                    </h3>
                    <div className="flex items-center gap-1 mt-1 text-athletic-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Shop</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 5. PERFORMANCE BENEFITS ============ */}
        <section className="relative py-20 lg:py-28 bg-ink-950 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img src={siteImages.powerBackground} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-ink-950 to-ink-950/70" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-7">
                <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Performance Pillars</p>
                <h2 className="font-display text-5xl lg:text-7xl tracking-wider leading-none">
                  BUILT FOR<br />
                  <span className="text-athletic-500">EVERY MOVE.</span>
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-white/70 leading-relaxed">
                  Every product is engineered to support the four pillars of athletic excellence. Because real performance demands real gear.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/10">
              {performancePillars.map((pillar, i) => (
                <div
                  key={i}
                  className="group relative p-8 border-r border-b border-white/10 hover:bg-athletic-500 transition-colors duration-300"
                >
                  <span className="absolute top-4 right-4 text-[11px] text-white/30 group-hover:text-white/90 font-mono">
                    0{i + 1}
                  </span>
                  <pillar.icon className="w-9 h-9 text-athletic-400 group-hover:text-white mb-5 transition-colors" strokeWidth={1.5} />
                  <h3 className="font-display text-3xl tracking-wider mb-3">
                    {pillar.title.toUpperCase()}
                  </h3>
                  <p className="text-white/60 group-hover:text-white/90 text-sm leading-relaxed transition-colors">
                    {pillar.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 6. WHY CHOOSE US ============ */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-14 max-w-2xl mx-auto">
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Why Sport Fitt Pro</p>
              <h2 className="font-display text-5xl lg:text-6xl tracking-wider leading-none">
                ATHLETES. NOT <span className="text-athletic-500">AMATEURS.</span>
              </h2>
              <p className="text-ink-500 mt-5">
                Four reasons serious lifters, runners and everyday athletes pick us over the rest.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Truck, title: 'Fast USA Shipping', desc: 'Free delivery on orders over $75 anywhere in the USA.' },
                { icon: ShieldCheck, title: 'Secure Checkout', desc: 'Bank-level encryption protects every transaction.' },
                { icon: Dumbbell, title: 'Quality Gear', desc: 'Performance-tested by athletes, engineered to last.' },
                { icon: Headphones, title: 'Real Support', desc: 'Actual humans who train — ready to help 24/7.' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="group relative bg-ink-50 p-8 border-b-4 border-transparent hover:border-athletic-500 hover:bg-ink-900 hover:text-white transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-athletic-500 group-hover:bg-volt-400 flex items-center justify-center mb-5 transition-colors">
                    <item.icon className="w-6 h-6 text-white group-hover:text-ink-900" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display text-2xl tracking-wider mb-3 leading-tight">
                    {item.title.toUpperCase()}
                  </h3>
                  <p className="text-sm text-ink-500 group-hover:text-white/80 leading-relaxed transition-colors">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 7. LIFESTYLE SPLIT SECTION ============ */}
        <section className="relative bg-ink-950 overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[400px] lg:min-h-[600px]">
              <img
                src={siteImages.lifestyleGym || siteImages.heroSecondary}
                alt="Gym training"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-ink-950/70 to-transparent" />
              <div className="relative p-10 lg:p-14 h-full flex flex-col justify-end text-white">
                <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Indoor Training</p>
                <h3 className="font-display text-4xl lg:text-5xl tracking-wider leading-none">
                  GYM. WEIGHTS.<br />
                  <span className="text-athletic-500">RESULTS.</span>
                </h3>
              </div>
            </div>
            <div className="relative min-h-[400px] lg:min-h-[600px]">
              <img
                src={siteImages.lifestyleOutdoor || siteImages.heroMain}
                alt="Outdoor training"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tl from-athletic-950/70 to-transparent" />
              <div className="relative p-10 lg:p-14 h-full flex flex-col justify-end text-white">
                <p className="text-volt-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Outdoor Training</p>
                <h3 className="font-display text-4xl lg:text-5xl tracking-wider leading-none">
                  RUN. CLIMB.<br />
                  <span className="text-volt-400">CONQUER.</span>
                </h3>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 8. CTA BANNER ============ */}
        <section className="relative py-24 lg:py-32 bg-athletic-500 overflow-hidden">
          <div className="absolute inset-0 opacity-25">
            <img src={siteImages.ctaBackground} alt="" className="w-full h-full object-cover mix-blend-multiply" />
          </div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(-45deg, transparent 0 60px, rgba(0,0,0,0.08) 60px 62px)'
          }} />

          <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
            <p className="text-white/80 text-[11px] font-black tracking-[0.5em] uppercase mb-4">
              Ready to Perform?
            </p>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl text-white leading-none tracking-wider mb-6">
              LEVEL UP<br />
              YOUR <span className="text-ink-950">TRAINING</span>
            </h2>
            <p className="text-white/90 text-lg max-w-xl mx-auto mb-10">
              Gear built for athletes. Shipped fast. Backed by a 30-day guarantee.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-3 px-10 py-5 bg-ink-950 hover:bg-white text-white hover:text-ink-950 font-black uppercase tracking-[0.2em] text-sm transition-all"
            >
              Shop The Collection
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
